import { Box, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { useMeQuery, VendorVerification } from "generated/graphql";
import { formatIsoDate } from "pages/formTools";
import { onboardingOpen } from "pages/dashboard/pages/onboarding/helpers";
import { useTranslation } from "react-i18next";

/**
 * The message for the signed-in vendor's state, if any: application under
 * review or sent back, paused or awaiting re-verification, expiring documents,
 * or onboarding to finish.
 */
const BannerContent: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const location = useLocation();
  const [{ data }] = useMeQuery();
  const vendor = data?.me?.vendor;
  const onOnboarding = location.pathname.startsWith("/dashboard/onboarding");

  // An approved vendor hidden from customers is told why.
  if (vendor?.pausedAt || vendor?.reverificationRequestedAt) {
    return (
      <Box
        role="status"
        sx={{
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.common.black,
          px: 2,
          py: 1,
          textAlign: "center",
        }}
      >
        <Typography variant="body2">
          {vendor.pausedAt
            ? vendor.pauseReason
              ? t("banner.paused_reason", { reason: vendor.pauseReason })
              : t("banner.paused")
            : t("banner.reverification", {
                reasons: vendor.reverificationReasons.join(", "),
              })}
        </Typography>
      </Box>
    );
  }

  if (vendor?.expiryReminderEnabled && vendor.expiringDocuments.length) {
    return (
      <Box
        role="status"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.common.black,
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="body2">
          {[
            ...vendor.expiringDocuments.map(({ label, expiryDate, daysLeft }) =>
              t(
                daysLeft < 0
                  ? "banner.document_expired"
                  : "banner.document_expires",
                {
                  document: label.toLowerCase(),
                  date: formatIsoDate(expiryDate),
                },
              ),
            ),
            t("banner.renew_documents"),
          ].join(" ")}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="primary"
          style={{ color: theme.palette.common.white }}
          onClick={() => navigate("/dashboard/profile/documents")}
        >
          {t("banner.update_documents")}
        </Button>
      </Box>
    );
  }

  if (!vendor) {
    return null;
  }

  if (vendor.verificationStatus === VendorVerification.Approved) {
    return !onboardingOpen(vendor) || onOnboarding ? null : (
      <Box
        role="status"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          backgroundColor: theme.palette.success.light,
          color: theme.palette.common.black,
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="body2">{t("banner.onboarding")}</Typography>
        <Button
          size="small"
          variant="contained"
          color="primary"
          style={{ color: theme.palette.common.white }}
          onClick={() => navigate("/dashboard/onboarding")}
        >
          {t("banner.continue_onboarding")}
        </Button>
      </Box>
    );
  }

  const rejected = vendor.verificationStatus === VendorVerification.Rejected;

  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        backgroundColor: rejected
          ? theme.palette.error.light
          : theme.palette.warning.light,
        color: theme.palette.common.black,
        px: 2,
        py: 1,
        textAlign: "center",
      }}
    >
      <Typography variant="body2">
        {rejected
          ? vendor.rejectionReason
            ? t("banner.rejected_reason", {
                business: vendor.businessName,
                reason: vendor.rejectionReason,
              })
            : t("banner.rejected", { business: vendor.businessName })
          : t("banner.processing", { business: vendor.businessName })}
      </Typography>
      {!rejected && onboardingOpen(vendor) && !onOnboarding && (
        <Button
          size="small"
          variant="contained"
          color="primary"
          style={{ color: theme.palette.common.white }}
          onClick={() => navigate("/dashboard/onboarding")}
        >
          {t("banner.continue_onboarding")}
        </Button>
      )}
      {rejected && (
        <Button
          size="small"
          variant="contained"
          color="primary"
          style={{ color: theme.palette.common.white }}
          onClick={() => navigate("/dashboard/application")}
        >
          {t("banner.edit_application")}
        </Button>
      )}
    </Box>
  );
};

export default BannerContent;
